<template>
  <div class="dashboard-pages">
    <v-layout row mb-2 justify-center>
      <v-flex xs3 pa-2 ma-2 green lighten-3 v-show="showBalanceDetail">
        <p class="subheading font-weight-regular mb-0">
          {{ $t('Total Debit') }}:
          <strong>{{ accountPayableInfo.total_debit | currency }} {{ systemCurrency }}</strong>
        </p>
      </v-flex>

      <v-flex xs3 pa-2 ma-2 red lighten-3 @click="showBalanceDetail = !showBalanceDetail">
        <p class="subheading font-weight-regular mb-0">
          {{ $t('Total Balance') }}:
          <strong>{{ accountPayableInfo.total_balance | currency }} {{ systemCurrency }}</strong>
        </p>
      </v-flex>

      <v-flex xs3 pa-2 ma-2 yellow lighten-3 v-show="showBalanceDetail">
        <p class="subheading font-weight-regular mb-0">
          {{ $t('Total Credit') }}:
          <strong>{{ accountPayableInfo.total_credit | currency }} {{ systemCurrency }}</strong>
        </p>
      </v-flex>
    </v-layout>

    <v-card class="pa-3">
      <v-layout row wrap justify-space-between>
        <dt-search-box
          :search="search"
          @typing="searchValue"
          @reset-datatable="resetDataTable()"
          @clear-search-field="search = null"
        />

        <v-layout row wrap justify-end class="pt-2 pb-2">
          <v-btn
            dark
            color="primary"
            v-if="hasPermissions(['payable_statement_store'])"
            @click="dialogs.addDialog = true"
          >
            <v-icon>add</v-icon>
            {{ $t('datatable-add-new') }}
          </v-btn>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card flat>
      <v-layout row wrap justify-end>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 0px; height: 30px; width: 30px"
              :loading="loadThePrintBtn"
              flat
              fab
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('print')"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Print Table`) }}</span>
        </v-tooltip>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 20px 5px 10px; height: 30px; width: 30px"
              fab
              :loading="loadTheExportBtn"
              flat
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('export')"
            >
              <v-icon>fa-file-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Export Table`) }}</span>
        </v-tooltip>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.payable_statement_id }}</td>-->
          <td>{{ props.item.payable_statement_auto_number }}</td>
          <td>{{ props.item.supplier_invoice_number }}</td>
          <td>{{ props.item.statement_date }}</td>
          <td>{{ props.item.amount | currency }}</td>
          <td>{{ props.item.balance | currency }}</td>
          <td>{{ props.item.paid | currency }}</td>
          <td>
            <v-tooltip top>
              <v-chip
                slot="activator"
                label
                :color="
                  props.item.payment_status === 'Paid'
                    ? 'success'
                    : props.item.payment_status === 'Paid Partially'
                    ? 'warning'
                    : 'error'
                "
                text-color="white"
                class="no-select"
              >
                <v-icon>{{
                  props.item.payment_status === 'Paid' || props.item.payment_status === 'Paid Partially'
                    ? 'attach_money'
                    : 'money_off'
                }}</v-icon>
              </v-chip>
              <span>{{
                props.item.payment_status === 'Paid'
                  ? $t('Paid')
                  : props.item.payment_status === 'Paid Partially'
                  ? $t('Paid Partially')
                  : $t('Not Paid')
              }}</span>
            </v-tooltip>
          </td>
          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip
                  v-if="hasPermissions(['payable_statement_show'])"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="viewRecord(props.item.payable_statement_id)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-view') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Statement Information`) }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="
                    hasPermissions(['payable_statement_update']) &&
                      props.item.editable === '1' &&
                      props.item.payment_status === 'Not Paid'
                  "
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="editRecord(props.item.payable_statement_id)">
                    <v-icon>edit</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-edit') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Edit Statement Information`) }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="hasPermissions(['single_statement_payment_store']) && props.item.payment_status !== 'Paid'"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="singlePayment(props.item)">
                    <v-icon>payment</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('Payment') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Add Payment For This Statement`) }}</span>
                </v-tooltip>

                <v-divider
                  v-if="
                    hasPermissions(['payable_statement_destroy']) &&
                      props.item.editable === '1' &&
                      props.item.payment_status === 'Not Paid'
                  "
                />

                <v-tooltip
                  v-if="
                    hasPermissions(['payable_statement_destroy']) &&
                      props.item.editable === '1' &&
                      props.item.payment_status === 'Not Paid'
                  "
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="showConfirmDeletion(props.item.payable_statement_id)">
                    <v-icon class="red--text">delete</v-icon>
                    <v-list-tile-content class="red--text px-3">{{ $t('datatable-delete') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('Delete Statement Record') }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <add-payable-statement
      :addDialog="dialogs.addDialog"
      :accountId="Number(accountPayableId)"
      @reset-datatable="resetDataTable(), resetAllErrors(true)"
      @hidemodal="resetAllErrors(true), (dialogs.addDialog = false)"
    />

    <edit-payable-statement
      :editDialog="dialogs.editDialog"
      :accountId="Number(accountPayableId)"
      @reset-datatable="resetDataTable()"
      @hidemodal="resetAllErrors(true), (dialogs.editDialog = false)"
    />

    <add-single-payment
      :paymentDialog="dialogs.paymentDialog"
      :statement="statement"
      :account="accountPayableInfo"
      @reset-datatable="resetDataTable(), showPayments()"
      @hidemodal="resetAllErrors(true), (dialogs.paymentDialog = false)"
    />

    <view-payable-statement :viewDialog="dialogs.viewDialog" @hidemodal="dialogs.viewDialog = false" />

    <confirm-deletion
      :deleteDialog="dialogs.deleteDialog"
      :btnDeleteLoading="btnDeleteLoading"
      @hidemodal="dialogs.deleteDialog = false"
      @confirm="deleteAction()"
    />

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    viewPayableStatement: () => import('./viewPayableStatement.vue'),
    addPayableStatement: () => import('./addPayableStatement.vue'),
    editPayableStatement: () => import('./editPayableStatement.vue'),
    addSinglePayment: () => import('./addSinglePayment.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue')
  },

  data() {
    return {
      accountPayableId: Number(this.$route.query.account_payable_id),
      statement: {},
      name: this.$route.query.name,
      systemCurrency: localStorage.systemCurrency,
      dialogs: {
        viewDialog: false,
        addDialog: false,
        editDialog: false,
        paymentDialog: false,
        deleteDialog: false,
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      deleteId: 0,
      btnDeleteLoading: false,
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'payable_statement_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'payable_statement_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Code'), value: 'payable_statement_auto_number' },
        {
          text: this.$t('Invoice No.'),
          value: 'payable_statement_auto_number'
        },
        { text: this.$t('Date'), value: 'statement_date' },
        {
          text: `${this.$t('Amount')} (${localStorage.systemCurrency})`,
          value: 'amount'
        },
        {
          text: `${this.$t('Balance')} (${localStorage.systemCurrency})`,
          value: 'balance'
        },
        {
          text: `${this.$t('Paid')} (${localStorage.systemCurrency})`,
          value: 'paid'
        },
        { text: this.$t('Status'), value: 'payment_status' }
      ],
      allowGettingData: true,
      showBalanceDetail: false
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: `${this.$t('Account Payable Statements')}: <strong class="title-highlight">${this.name}</strong>`,
      icon: 'attach_money'
    })

    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }

    this.fetchRemittances()
  },

  computed: {
    ...mapGetters({
      resultData: 'accountStatements/getStatementList',
      accountPayableInfo: 'accountPayable/getAccountPayableInfo'
    })
  },

  watch: {
    pagination: {
      immediate: false,
      deep: false,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'accountStatements/fetchStatementList',
      fetchViewInfo: 'accountStatements/fetchStatementInfo',
      fetchAccountPayableInfo: 'accountPayable/fetchAccountPayableInfo',
      fetchRemittances: 'remittances/setFullRemittanceOfficesList',
      deleteRecord: 'accountStatements/deleteStatement'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get(`payable_statement_index/${this.accountPayableId}`, {
        params: { is_paginated: 0, search: this.search }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions([
        'payable_statement_show',
        'payable_statement_update',
        'payable_statement_destroy',
        'single_statement_payment_store'
      ])
    },

    updateDatatableResult() {
      this.loading = true
      let payload = {
        ...this.pagination,
        search: this.search,
        id: this.accountPayableId
      }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
      this.fetchAccountPayableInfo(this.accountPayableId)
    },

    resetDataTable() {
      this.allowGettingData = true

      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    },

    viewRecord(id) {
      this.fetchViewInfo(id).then(() => {
        this.dialogs.viewDialog = true
      })
    },

    editRecord(id) {
      this.fetchViewInfo(id).then(() => {
        this.dialogs.editDialog = true
      })
    },

    singlePayment(statement) {
      this.fetchViewInfo(statement.payable_statement_id).then(() => {
        this.statement = statement
        this.dialogs.paymentDialog = true
      })
    },

    showConfirmDeletion(id) {
      this.deleteId = id
      this.dialogs.deleteDialog = true
    },

    deleteAction() {
      this.btnDeleteLoading = true
      this.deleteRecord(this.deleteId)
        .then(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
          let payload = {
            ...this.pagination,
            search: this.search,
            id: this.accountPayableId
          }
          this.updateResult(payload)
          this.fetchAccountPayableInfo(this.accountPayableId)
        })
        .catch(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
        })
    },

    showPayments() {
      this.$router.push({
        name: 'accounting-account-payable-payments',
        query: {
          account_payable_id: this.accountPayableInfo.account_payable_id,
          name: this.accountPayableInfo.vender_name,
          commission: false
        }
      })
    }
  }
}
</script>

<style scoped />
